import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Calculator, DollarSign, Home as HomeIcon, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CostEstimate = () => {
  const [formData, setFormData] = useState({
    sqft: '',
    style: 'ranch',
    finishLevel: 'standard',
    lotLocation: 'columbus',
    name: '',
    email: '',
    phone: ''
  });
  const [estimate, setEstimate] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const calculateEstimate = async (e) => {
    e.preventDefault();
    
    const sqft = parseInt(formData.sqft);
    if (isNaN(sqft) || sqft < 1000) {
      toast.error('Please enter a valid square footage (minimum 1,000 sq ft)');
      return;
    }

    // Base cost per sqft based on style
    const styleCosts = {
      'ranch': 180,
      'two-story': 165,
      'modern-farmhouse': 195,
      'duplex': 170
    };

    // Finish level multipliers
    const finishMultipliers = {
      'standard': 1.0,
      'premium': 1.15,
      'luxury': 1.30
    };

    // Location adjustments
    const locationAdjustments = {
      'columbus': 1.0,
      'delaware': 1.05,
      'dublin': 1.08,
      'westerville': 1.03
    };

    const baseCost = styleCosts[formData.style] || 180;
    const finishMultiplier = finishMultipliers[formData.finishLevel] || 1.0;
    const locationMultiplier = locationAdjustments[formData.lotLocation] || 1.0;

    const totalCostPerSqft = baseCost * finishMultiplier * locationMultiplier;
    const lowEstimate = Math.round(sqft * totalCostPerSqft * 0.95);
    const highEstimate = Math.round(sqft * totalCostPerSqft * 1.05);

    const estimateData = {
      low: lowEstimate.toLocaleString(),
      high: highEstimate.toLocaleString(),
      sqft: sqft.toLocaleString(),
      costPerSqft: Math.round(totalCostPerSqft),
      rawLow: lowEstimate,
      rawHigh: highEstimate
    };

    setEstimate(estimateData);
    setShowContactForm(true);
    toast.success('Cost estimate calculated!');

    // Save to backend (anonymous calculation)
    try {
      await axios.post(`${API}/cost-estimate`, {
        name: formData.name || null,
        email: formData.email || null,
        phone: formData.phone || null,
        sqft: sqft,
        style: formData.style,
        finish_level: formData.finishLevel,
        lot_location: formData.lotLocation,
        estimated_low: lowEstimate,
        estimated_high: highEstimate,
        cost_per_sqft: Math.round(totalCostPerSqft)
      });
    } catch (error) {
      console.error('Error saving estimate:', error);
    }
  };

  const submitWithContact = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in your contact information');
      return;
    }

    try {
      await axios.post(`${API}/cost-estimate`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        sqft: parseInt(formData.sqft),
        style: formData.style,
        finish_level: formData.finishLevel,
        lot_location: formData.lotLocation,
        estimated_low: estimate.rawLow,
        estimated_high: estimate.rawHigh,
        cost_per_sqft: estimate.costPerSqft
      });

      toast.success('Thank you! We\'ll send you a detailed quote within 24 hours.');
      setShowContactForm(false);
    } catch (error) {
      console.error('Error submitting contact info:', error);
      toast.error('Failed to submit. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <Calculator className="text-[#DAA520] mx-auto mb-6" size={64} />
            <h1 className="text-5xl md:text-6xl font-bold text-[#262626] mb-6">
              Cost Estimate Tool
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Get a preliminary estimate for your custom home build. This calculator provides an approximate range based on your inputs.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <Card className="border-2 border-gray-100 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[#262626] mb-6">
                    Enter Your Project Details
                  </h2>
                  <form onSubmit={calculateEstimate} className="space-y-6">
                    <div>
                      <Label htmlFor="sqft" className="text-[#262626] font-medium mb-2">
                        Square Footage *
                      </Label>
                      <Input
                        id="sqft"
                        name="sqft"
                        type="number"
                        value={formData.sqft}
                        onChange={handleChange}
                        required
                        min="1000"
                        placeholder="e.g., 2400"
                        className="border-gray-300 focus:border-[#DAA520] focus:ring-[#DAA520]"
                      />
                      <p className="text-sm text-gray-500 mt-1">Minimum 1,000 sq ft</p>
                    </div>

                    <div>
                      <Label htmlFor="style" className="text-[#262626] font-medium mb-2">
                        Home Style *
                      </Label>
                      <select
                        id="style"
                        name="style"
                        value={formData.style}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#DAA520] focus:ring-[#DAA520] focus:outline-none"
                      >
                        <option value="ranch">Ranch (Single Story)</option>
                        <option value="two-story">Two-Story Traditional</option>
                        <option value="modern-farmhouse">Modern Farmhouse</option>
                        <option value="duplex">Duplex Investment</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="finishLevel" className="text-[#262626] font-medium mb-2">
                        Finish Level *
                      </Label>
                      <select
                        id="finishLevel"
                        name="finishLevel"
                        value={formData.finishLevel}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#DAA520] focus:ring-[#DAA520] focus:outline-none"
                      >
                        <option value="standard">Standard Finishes</option>
                        <option value="premium">Premium Finishes (+15%)</option>
                        <option value="luxury">Luxury Finishes (+30%)</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Includes materials, fixtures, and appliances
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="lotLocation" className="text-[#262626] font-medium mb-2">
                        Lot Location *
                      </Label>
                      <select
                        id="lotLocation"
                        name="lotLocation"
                        value={formData.lotLocation}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-[#DAA520] focus:ring-[#DAA520] focus:outline-none"
                      >
                        <option value="columbus">Columbus</option>
                        <option value="delaware">Delaware (+5%)</option>
                        <option value="dublin">Dublin (+8%)</option>
                        <option value="westerville">Westerville (+3%)</option>
                      </select>
                      <p className="text-sm text-gray-500 mt-1">
                        Location affects material delivery and labor costs
                      </p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white py-6 text-lg font-semibold"
                    >
                      Calculate Estimate
                      <Calculator className="ml-2" size={20} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Results */}
            <div>
              {estimate ? (
                <div className="space-y-6">
                  <Card className="border-2 border-[#DAA520] shadow-xl">
                    <CardContent className="p-8">
                      <div className="flex items-center space-x-3 mb-6">
                        <DollarSign className="text-[#DAA520]" size={32} />
                        <h3 className="text-2xl font-bold text-[#262626]">
                          Your Estimated Range
                        </h3>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-6 mb-6">
                        <div className="text-center mb-4">
                          <div className="text-5xl font-bold text-[#DAA520] mb-2">
                            ${estimate.low} - ${estimate.high}
                          </div>
                          <div className="text-gray-600">Total Project Cost</div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-center pt-4 border-t border-gray-200">
                          <div>
                            <div className="text-2xl font-bold text-[#262626]">{estimate.sqft}</div>
                            <div className="text-sm text-gray-600">Square Feet</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#262626]">${estimate.costPerSqft}</div>
                            <div className="text-sm text-gray-600">Per Sq Ft</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="w-2 h-2 bg-[#DAA520] rounded-full mr-2"></span>
                          Includes all construction costs
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="w-2 h-2 bg-[#DAA520] rounded-full mr-2"></span>
                          Fixed-price contract protection
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <span className="w-2 h-2 bg-[#DAA520] rounded-full mr-2"></span>
                          Warranty coverage included
                        </div>
                      </div>

                      <Link to="/contact">
                        <Button className="w-full bg-[#262626] hover:bg-[#DAA520] text-white py-3">
                          Get Detailed Quote
                          <ArrowRight className="ml-2" size={18} />
                        </Button>
                      </Link>

                      {showContactForm && (
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <h4 className="font-semibold text-[#262626] mb-4">Get Your Detailed Quote</h4>
                          <div className="space-y-3">
                            <Input
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="Your Name"
                            />
                            <Input
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="Email Address"
                            />
                            <Input
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="Phone Number"
                            />
                            <Button
                              onClick={submitWithContact}
                              className="w-full bg-[#DAA520] hover:bg-[#B8860B] text-white"
                            >
                              Send Me Details
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-[#DAA520]/10 border-0">
                    <CardContent className="p-6">
                      <h4 className="font-semibold text-[#262626] mb-2">
                        Important Notes:
                      </h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li>• This is a preliminary estimate only</li>
                        <li>• Actual costs vary based on specific design choices</li>
                        <li>• Does not include land purchase or site preparation</li>
                        <li>• Schedule consultation for accurate pricing</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="border-2 border-gray-100 h-full flex items-center justify-center">
                  <CardContent className="p-12 text-center">
                    <HomeIcon className="text-gray-300 mx-auto mb-4" size={80} />
                    <h3 className="text-xl font-semibold text-gray-700 mb-2">
                      Enter Your Details
                    </h3>
                    <p className="text-gray-500">
                      Fill out the form to calculate your estimated build cost
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#262626] mb-4">
              What's Included in Your Build Cost
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Construction',
                items: ['Foundation & framing', 'Roofing & siding', 'Windows & doors', 'HVAC systems', 'Plumbing & electrical', 'Insulation']
              },
              {
                title: 'Interior Finishes',
                items: ['Drywall & paint', 'Flooring throughout', 'Kitchen cabinets', 'Bathroom fixtures', 'Lighting fixtures', 'Trim & millwork']
              },
              {
                title: 'Project Management',
                items: ['Permits & inspections', 'Project supervision', 'Quality control', 'Subcontractor coordination', 'Timeline management', 'Final walkthrough']
              }
            ].map((category, index) => (
              <Card key={index} className="border-2 border-gray-100">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-[#262626] mb-4">{category.title}</h3>
                  <ul className="space-y-2">
                    {category.items.map((item, i) => (
                      <li key={i} className="flex items-start text-gray-700">
                        <span className="w-1.5 h-1.5 bg-[#DAA520] rounded-full mr-2 mt-2 flex-shrink-0"></span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#DAA520]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">
            Ready for an Accurate Quote?
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Schedule a consultation to discuss your project in detail and receive a comprehensive, fixed-price proposal.
          </p>
          <Link to="/contact">
            <Button className="bg-[#262626] hover:bg-black text-white px-8 py-6 text-lg">
              Schedule Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CostEstimate;
